import { apiClient, unwrap, unwrapEnvelope } from './client';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type { Review, CreateReviewInput } from '@/types/review';

export interface ReviewListResult {
  items: Review[];
  pagination: PaginationMeta;
}

export const reviewsApi = {
  async forProduct(
    productId: number,
    page = 1,
    limit = 10
  ): Promise<ReviewListResult> {
    const res = await apiClient.get<ApiSuccess<Review[]>>(
      `/products/${productId}/reviews`,
      { params: { page, limit } }
    );
    const env = unwrapEnvelope(res);
    return {
      items: env.data,
      pagination: env.pagination ?? {
        page,
        limit,
        total: env.data.length,
        totalPages: 1,
        nextPage: null,
      },
    };
  },

  async create(input: CreateReviewInput): Promise<Review> {
    const res = await apiClient.post<ApiSuccess<Review>>('/reviews', input);
    return unwrap(res);
  },

  async update(
    id: number,
    input: Partial<Pick<Review, 'rating' | 'title' | 'comment'>>
  ): Promise<Review> {
    const res = await apiClient.patch<ApiSuccess<Review>>(`/reviews/${id}`, input);
    return unwrap(res);
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`/reviews/${id}`);
  },

  async listMine(page = 1, limit = 10): Promise<ReviewListResult> {
    const res = await apiClient.get<ApiSuccess<Review[]>>('/reviews/me', {
      params: { page, limit },
    });
    const env = unwrapEnvelope(res);
    return {
      items: env.data,
      pagination: env.pagination ?? {
        page,
        limit,
        total: env.data.length,
        totalPages: 1,
        nextPage: null,
      },
    };
  },
};
